"use client";
import IconAI from "@/components/icons/IconAI";
import { OPEN_AI_FULL } from "@/constants/common";
import useDeviceDetect from "@/hooks/useMobile";
import RemoveIcon from "@mui/icons-material/Remove";
import { Typography } from "@mui/material";
import clsx from "clsx";
import { getCookie, setCookie } from "cookies-next";
import { AnimatePresence, easeIn, easeOut, motion } from "framer-motion";
import moment from "moment";
import { useTranslations } from "next-intl";
import React, { memo, useEffect, useState } from "react";
interface SessionAi {
  expired_at: number;
  created_at: number;
}
const subtitleVariants = {
  hidden: {
    maxHeight: 0,
    opacity: 0,
    transition: { duration: 0.18, ease: easeOut },
  },
  visible: {
    maxHeight: 120,
    opacity: 1,
    transition: { duration: 0.2, ease: easeIn },
  },
};
const Button = memo(function Button({
  toggle,
  open,
}: {
  toggle: () => void;
  open: boolean;
}) {
  const t = useTranslations("messages");
  const isMobile = useDeviceDetect();
  const [isOpenAiFull, setIsOpenAiFull] = useState<boolean>(false);
  useEffect(() => {
    const item = getCookie(OPEN_AI_FULL);
    const itemParse = item ? (JSON.parse(item as string) as SessionAi) : null;
    const now = moment();
    if (itemParse && moment(itemParse?.expired_at).isAfter(now)) {
      setIsOpenAiFull(false);
    } else {
      if (isMobile) return;
      const timer = setTimeout(() => {
        setIsOpenAiFull(true);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClickZoomIn = (
    event: React.MouseEvent<SVGSVGElement, MouseEvent>
  ) => {
    event.stopPropagation();
    setIsOpenAiFull((prev) => !prev);
    const now = moment();
    setCookie(
      OPEN_AI_FULL,
      JSON.stringify({
        created_at: now.valueOf(),
        expired_at: now.clone().add(1, "days").valueOf(),
      }),
      {
        path: "/",
        sameSite: "lax",
      }
    );
  };

  return (
    <motion.button
      layout
      initial={{
        width: isOpenAiFull ? 320 : 200,
        // height: isOpenAiFull ? 96 : 64,
        padding: isOpenAiFull ? "24px" : "16px 24px 16px 16px",
      }}
      animate={{
        width: isOpenAiFull ? 320 : 200,
        // height: isOpenAiFull ? 96 : 64,
        padding: isOpenAiFull ? "24px" : "16px 24px 16px 16px",
      }}
      whileTap={{ scale: 0.95 }}
      whileHover={{ scale: isMobile ? 1 : 1.05 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.3,
      }}
      aria-label={open ? "Close Chat" : "Open Chat"}
      onClick={toggle}
      className={clsx(
        `relative shadow-[0px_2px_16px_0px_#00000026] !bg-white cursor-pointer flex !rounded-[16px] !min-w-[192px]  justify-start items-center gap-4  border-none`
      )}
    >
      {isOpenAiFull && (
        <RemoveIcon
          onClick={handleClickZoomIn}
          className="cursor-pointer absolute top-[0.25rem] right-[0.85rem] !text-[#A4A4A4] hover:!text-[#727171]"
        />
      )}
      <IconAI
        height={32}
        width={32}
        viewBox="0 0 32 32"
        sx={{
          width: "32px",
          height: "32px",
        }}
      />
      <motion.div
        layout
        initial={{ height: "auto", width: "auto", opacity: 1 }}
        animate={{ height: "auto", width: "auto", opacity: 1 }}
        transition={{
          ease: "easeInOut",
          duration: 0.4,
          delay: 0.1,
          type: "spring",
          stiffness: 300,
          damping: 30,
        }}
        className={`flex !items-start !justify-start ${isOpenAiFull ? "gap-1" : ""} max-w-[230px] flex-col`}
      >
        <Typography
          component="span"
          className="!font-semibold !text-[16px] !leading-[100%] !text-[#1F61A9]"
          sx={{
            fontFamily: "Inter",
          }}
          translation-key={"copilotkit_assistant_title"}
        >
          {t("copilotkit_assistant_title")}
        </Typography>
        <AnimatePresence mode="sync">
          {isOpenAiFull && (
            <motion.div
              key="subtitle"
              variants={subtitleVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{ overflow: "hidden" }}
              className="w-full text-left"
            >
              <Typography
                component="span"
                className="!font-[400] !text-[14px] !leading-[100%] !text-[#767676]"
                sx={{
                  fontFamily: "Inter",
                  fontStyle: "Regular",
                  textAlign: "left",
                }}
                translate-key={"copilotkit_assistant_subtitle"}
              >
                {t("copilotkit_assistant_subtitle")}
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.button>
  );
});

export default Button;
